import type { Ref } from 'vue'

import {
  PlayerTagAssignDocument,
  PlayerTagUnassignDocument,
  RaidPlayerTagAssignDocument,
  RaidPlayerTagUnassignDocument,
} from '../../lib/handWrittenGraphql.ts'
import type {
  PlayerTagAssignMutation,
  PlayerTagUnassignMutation,
  RaidPlayerTagAssignMutation,
  RaidPlayerTagUnassignMutation,
} from '../../lib/handWrittenGraphql.ts'
import { MAX_TAGS_PER_PLAYER } from '../../lib/boardInvariants.ts'
import type { Player, PlayerTag, RaidId, RaidPlayer, RaidStageId } from '../../types/index.ts'

function snowflakeVar(s: string): string {
  return s
}

/**
 * Result-returning mutation runner signature. Mirrors
 * `useMutationRunners.runReporting` so swapping the runner in
 * `gvgPlannerStore.ts` is a one-line wiring concern.
 */
type ReportingRunner = <T>(
  mutation: unknown,
  variables: Record<string, unknown>,
  acknowledge?: (result: T) => boolean,
) => Promise<boolean>

/**
 * Read-only ctx the assignments composable needs. The per-player +
 * per-guild shape stays local here; the catalog CRUD in
 * `tags/actions.ts` does not need any of these.
 */
export interface TagAssignmentCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly error: Ref<string | null>
  readonly players: Ref<ReadonlyArray<Player>>
  readonly activeRaidId: Ref<RaidId | null>
  readonly activeStageId: Ref<RaidStageId | null>
  readonly raidPlayers: Ref<ReadonlyArray<RaidPlayer>>
  readonly playerTags: Ref<ReadonlyArray<PlayerTag>>
  readonly activeTagFilter: Ref<string | null>
  readonly runReporting: ReportingRunner
}

export interface TagAssignmentApi {
  readonly isActiveTagFilter: (tagId: string) => boolean
  readonly setActiveTagFilter: (tagId: string | null) => void
  /**
   * Async tag-assignment dispatch. Returns `Promise<boolean>`:
   *   - `true` once the mutation has been accepted (the authoritative
   *     board arrives through the subscription),
   *   - `false` if the mutation rejected, returned an empty
   *     payload, or the per-player `MAX_TAGS_PER_PLAYER` pre-flight cap
   *     was hit (with `error.value` set to a human-readable message).
   *
   * The earlier sync-`boolean` shape was a no-op fire-and-forget that
   * the UI could not observe. Switching to `Promise<boolean>` lets the
   * `PlayerTagStrip` disable the `+ Tag` button until the dispatch
   * resolves and surface a visible error if it returns `false`.
  */
  readonly assignTag: (playerId: string, tagId: string) => Promise<boolean>
  /** Same acknowledgement contract as assignTag for selector pending state. */
  readonly unassignTag: (playerId: string, tagId: string) => Promise<boolean>
}

/**
 * Owns the per-player <-> catalog wiring: assigning / unassigning
 * tags and the active-filter setter. The MAX_TAGS_PER_PLAYER pre-flight
 * cap (mirrors the backend invariant) lives here too — a future
 * refactor that re-introduces the cap check inside `assignTag` can be
 * pinned by the source-level guard in `lib/__tests__/playerTags.test.ts`
 * (`assignTag must consult MAX_TAGS_PER_PLAYER before dispatching`).
 *
 * The dispatcher `playerTags.value` (the catalog) and
 * `players.value` (the per-player tag arrays) are both reachable via
   * the ctx — `assignTag` reads the per-player tag count to enforce the
   * cap, while both mutation paths wait for transport acknowledgement. The
   * subscription replaces the per-player tag arrays. The
 * filter setter reads `activeTagFilter` so the chip strip can both
 * query (is-active) and clear (set to null).
 */
export function useTagAssignmentActions(ctx: TagAssignmentCtx): TagAssignmentApi {
  function isActiveTagFilter(tagId: string): boolean {
    return ctx.activeTagFilter.value === tagId
  }

  function setActiveTagFilter(tagId: string | null): void {
    ctx.activeTagFilter.value = tagId
  }

  /**
   * Assign `tagId` to `playerId`. The pre-flight `MAX_TAGS_PER_PLAYER`
   * cap mirrors the backend invariant — if the player already carries
   * the maximum, we write a human-readable message to `error.value`
   * and resolve `false` without firing the mutation (no round-trip
   * cost, and the UI's pending state can clear immediately).
   *
   * On the happy path the runner resolves `true`; on GraphQL/network
   * failure (or an empty payload from the server) it resolves `false`
   * with `error.value` set. State changes arrive through the subscription
   * rather than an additional board query.
   *
   * `guildId` stays a string through `snowflakeVar`, consistent with every
   * other tag action in this module. Apollo's request rewriter serializes it
   * as a raw JSON number at the network boundary, preserving the full
   * 64-bit Discord snowflake without JavaScript number rounding.
   */
  async function assignTag(playerId: string, tagId: string): Promise<boolean> {
    const raidId = ctx.activeRaidId.value
    const already = raidId === null
      ? ctx.players.value.find((p) => p.id === playerId)?.tags.length ?? 0
      : ctx.raidPlayers.value.find((p) => p.id === playerId)?.tags.length ?? 0
    if (already >= MAX_TAGS_PER_PLAYER) {
      ctx.error.value = `A player can carry at most ${String(MAX_TAGS_PER_PLAYER)} tags.`
      return false
    }
    if (raidId !== null) {
      return ctx.runReporting<RaidPlayerTagAssignMutation>(RaidPlayerTagAssignDocument, {
        guildId: snowflakeVar(ctx.activeDiscordGuildId.value),
        raidId: Number(raidId),
        raidPlayerId: Number(playerId),
        tagId: Number(tagId),
        stageId: ctx.activeStageId.value === null ? null : Number(ctx.activeStageId.value),
      }, (result) => result.raidPlayerTagAssign)
    }
    return ctx.runReporting<PlayerTagAssignMutation>(PlayerTagAssignDocument, {
      guildId: snowflakeVar(ctx.activeDiscordGuildId.value),
      playerId: Number(playerId),
      tagId: Number(tagId),
    })
  }

  function unassignTag(playerId: string, tagId: string): Promise<boolean> {
    const raidId = ctx.activeRaidId.value
    if (raidId !== null) {
      return ctx.runReporting<RaidPlayerTagUnassignMutation>(RaidPlayerTagUnassignDocument, {
        guildId: snowflakeVar(ctx.activeDiscordGuildId.value),
        raidId: Number(raidId),
        raidPlayerId: Number(playerId),
        tagId: Number(tagId),
        stageId: ctx.activeStageId.value === null ? null : Number(ctx.activeStageId.value),
      }, (result) => result.raidPlayerTagUnassign)
    }
    return ctx.runReporting<PlayerTagUnassignMutation>(PlayerTagUnassignDocument, {
      guildId: snowflakeVar(ctx.activeDiscordGuildId.value),
      playerId: Number(playerId),
      tagId: Number(tagId),
    })
  }

  return {
    isActiveTagFilter,
    setActiveTagFilter,
    assignTag,
    unassignTag,
  }
}
